<template>
  <div class="profile-area" ref="profileRoot">
    <button class="profile-btn" @click="toggleProfileMenu">
      <img class="profile-img" :src="profileImage" alt="Profile" />
    </button>

    <div v-if="showProfileMenu" class="profile-menu">
      <div class="profile-menu-header">
        <img class="profile-img-lg" :src="profileImage" alt="Profile" />

        <div class="profile-meta">
          <div class="profile-name">{{ profileName }}</div>
          <div v-if="impersonating" class="profile-sub profile-sub--imp"><i class="fa-solid fa-eye"></i> Viewing as</div>
        </div>
      </div>

      <!-- Same "View as" entries as swiftMarketing's user menu. -->
      <div class="profile-menu-items">
        <div v-if="showImpersonate" class="profile-item" @click="emitOpenImpersonate">
          <i class="fa-light fa-user-secret profile-item-icon"></i>
          {{ impersonating ? 'View as someone else…' : 'View as…' }}
        </div>

        <div v-if="impersonating" class="profile-item danger" @click="emitStopImpersonate">
          <i class="fa-light fa-eye-slash profile-item-icon"></i>
          Stop viewing as {{ impersonatingName || 'them' }}
        </div>

        <div class="profile-divider"></div>

        <div class="profile-item" @click="emitLogout">
          Sign out
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from "../api/api.service.js"
import bus from "@/helpers/appBus.js"

export default {
  name: "ProfileCardMenu",
  props: {
    azureId: { type: String, default: "" },
    token: { type: String, default: "" },
    showImpersonate: { type: Boolean, default: false },
    impersonating: { type: Boolean, default: false },
    impersonatingName: { type: String, default: "" }
  },
  data() {
    return {
      showProfileMenu: false,
      profileImage: "",
      profileName: "User",
      loadId: 0
    }
  },
  watch: {
    azureId() {
      this.loadProfile()
    },
    token() {
      this.loadProfile()
    },
    impersonating() {
      this.loadProfile()
    }
  },
  mounted() {
    this.loadProfile()
    bus.on("auth-context-ready", this.loadProfile)
    document.addEventListener("click", this.onClickOutside, true)
  },
  beforeUnmount() {
    bus.off("auth-context-ready", this.loadProfile)
    document.removeEventListener("click", this.onClickOutside, true)
  },
  methods: {
    toggleProfileMenu() {
      this.showProfileMenu = !this.showProfileMenu
    },
    onClickOutside(e) {
      if (!this.showProfileMenu) return
      const root = this.$refs.profileRoot
      if (!root) return
      if (!root.contains(e.target)) this.showProfileMenu = false
    },
    emitOpenImpersonate() {
      this.$emit("open-impersonate")
      this.showProfileMenu = false
    },
    emitStopImpersonate() {
      this.$emit("stop-impersonate")
      this.showProfileMenu = false
    },
    emitLogout() {
      this.$emit("logout")
      this.showProfileMenu = false
    },
    async loadProfile() {
      const id = ++this.loadId
      const azureIdToUse = this.azureId || localStorage.getItem("azure_id") || ""
      const tokenToUse = this.token

      if (!azureIdToUse || !tokenToUse) {
        const name = this.impersonating ? "Impersonating" : "User"
        if (id !== this.loadId) return
        this.profileName = name
        this.profileImage = this.makeInitialAvatar(name)
        return
      }

      try {
        // Prefer cached azure data written by App.vue
        let user = null
        const cached = localStorage.getItem("user_azure_data")
        if (cached) {
          try { user = JSON.parse(cached) } catch { user = null }
        }
        // Fallback to API (deduped by api.service cache)
        if (!user) {
          const userResult = await apiService.GetUsersAzureData(azureIdToUse, tokenToUse)
          if (id !== this.loadId) return
          user = userResult?.data?.data
        }

        const name =
          user?.displayName ||
          user?.mail ||
          user?.userPrincipalName ||
          "User"

        this.profileName = name

        try {
          const photoRes = await apiService.GetUserProfileImage(azureIdToUse, tokenToUse)
          if (id !== this.loadId) return
          const img = photoRes?.data?.data

          if (img && typeof img === "string" && img.startsWith("data:image")) {
            this.profileImage = img
          } else {
            this.profileImage = this.makeInitialAvatar(name)
          }
        } catch {
          if (id !== this.loadId) return
          this.profileImage = this.makeInitialAvatar(name)
        }
      } catch {
        if (id !== this.loadId) return
        const name = this.impersonating ? "Impersonating" : "User"
        this.profileName = name
        this.profileImage = this.makeInitialAvatar(name)
      }
    },
    makeInitialAvatar(name) {
      const initial = (name || "U").trim().charAt(0).toUpperCase()
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80">
        <rect width="100%" height="100%" rx="40" ry="40" fill="#2f4f6f"/>
        <text x="50%" y="55%" font-size="36" text-anchor="middle" fill="white" font-family="Arial" font-weight="600">${initial}</text>
      </svg>`
      return `data:image/svg+xml;base64,${btoa(svg)}`
    }
  }
}
</script>

<style scoped>
.profile-area {
  position: relative;
  max-height: 42px;
}

.profile-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0 10px;
}

.profile-img {
  width: 42px;
  height: 42px;
  border-radius: 999px;
  object-fit: cover;
/*  border: 2px solid var(--accent);*/
}

.profile-menu {
  position: absolute;
  top: 52px;
  right: 0;
  width: 250px;
  background: white;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  z-index: 999999;
}

.profile-menu-header {
  display: flex;
  gap: 12px;
  padding: 14px;
  border-bottom: 1px solid #e5e5e5;
  align-items: center;
}

.profile-img-lg {
  width: 54px;
  height: 54px;
  border-radius: 999px;
  object-fit: cover;
/*  border: 2px solid var(--accent);*/
}

.profile-meta {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.profile-name {
  font-weight: 700;
  font-size: 1.1rem;
  color: var(--primary);
}

.profile-sub {
  font-size: 0.9rem;
  color: #666;
}

.profile-sub--imp {
  color: #b45309;
  font-weight: 600;
}

.profile-item-icon {
  width: 18px;
  margin-right: 6px;
  text-align: center;
}

.profile-menu-items {
  padding: 8px 0;
}

.profile-item {
  padding: 12px 14px;
  font-size: 1rem;
  cursor: pointer;
  color: var(--primary);
}

.profile-item:hover {
  background: var(--accent-ltr);
}

.profile-item.danger {
  color: #c62828;
  font-weight: 600;
}

.profile-divider {
  height: 1px;
  background: #e5e5e5;
  margin: 6px 0;
}
</style>
